import React, { useEffect, useState } from "react";
import moment from "moment";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
  Radar,
  RadarChart,
  Dot,
  Tooltip,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import { getColor } from "../../tools";
import { Box } from "@mui/material";

const ticks = new Array(24)
  .fill(0)
  .map((_, index) => (index < 10 ? `0${index}:00` : `${index}:00`));
const hours = {
  morning: [
    "12",
    "01",
    "02",
    "03",
    "04",
    "05",
    "06",
    "07",
    "08",
    "09",
    "10",
    "11",
  ],
  noon: [
    "12",
    "13",
    "14",
    "15",
    "16",
    "17",
    "18",
    "19",
    "20",
    "09",
    "10",
    "11",
  ],
  evening: [
    "00",
    "01",
    "02",
    "03",
    "16",
    "17",
    "18",
    "19",
    "20",
    "21",
    "22",
    "23",
  ],
  night: [
    "00",
    "01",
    "02",
    "03",
    "04",
    "05",
    "06",
    "07",
    "08",
    "21",
    "22",
    "23",
  ],
};
const interval = 15; // om de 15 minuten
const minutes = ["00", "15", "30", "45"];
const labelWidth = 150;
const answerWidth = 300;
function CustomTooltip(props) {
  const { cx, active, payload } = props;
  const payloadArr = payload?.[0]?.payload ?? {};
  const infoKeys = Object.keys(payloadArr).filter((key) =>
    key.includes("#info")
  );
  // show the latest information first and limit to 4
  const info = infoKeys
    .map((key) => payloadArr[key])
    .reverse()
    .slice(0, 4);

  if (!active || info.length === 0) return null;
  return (
    <Paper style={{ padding: "16px", zIndex: 3 }} elevation={15}>
      {info.map(({ answer, sendBeep, qualitativeAnswers }, index) => (
        <div key={index} style={{ paddingTop: 8 }}>
          <Typography variant="subtitle2">
            {moment.utc(sendBeep).format("ddd DD/MM")}
          </Typography>
          <div style={{ display: "flex" }}>
            <div
              style={{
                height: "12px",
                width: "12px",
                backgroundColor: getColor(answer.id),
                marginRight: "8px",
                marginTop: "3px",
                borderRadius: "50%",
                display: "flex",
              }}
            ></div>
            <div style={{ minWidth: 100 }}>
              <Box fontStyle="italic" width={labelWidth}>
                <Typography variant="body2" noWrap>
                  {answer.label}
                </Typography>
              </Box>
            </div>
            <div style={{ maxWidth: answerWidth, display: "flex", gap: 4 }}>
              <Typography variant="subtitle2">{answer.primaryScore}</Typography>
              <Typography variant="body2">({answer.score}%)</Typography>
            </div>
          </div>
          {qualitativeAnswers.map((qualitativeAnswer, index) => (
            <div
              key={`ClockBoardQualitative${index}`}
              style={{ display: "flex" }}
            >
              <div
                style={{
                  height: "12px",
                  width: "12px",
                  marginRight: "8px",
                  marginTop: "3px",
                  borderRadius: "50%",
                  display: "flex",
                }}
              ></div>
              <div style={{ minWidth: 100 }}>
                <Box fontStyle="italic" width={labelWidth}>
                  <Typography variant="body2" noWrap>
                    {qualitativeAnswer.label}
                  </Typography>
                </Box>
              </div>
              <div>
                <Typography
                  variant="body2"
                  style={{ maxWidth: answerWidth, wordBreak: "break-word" }}
                >
                  {qualitativeAnswer.score}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      ))}
    </Paper>
  );
}

const CustomizedDot = (props) => {
  const { cx, cy, fill, fillColor } = props;
  const color = props?.payload?.payload?.[fillColor];
  return <Dot cx={cx} cy={cy} r={5} radius={5} fill={color} />;
};
const radius = 150;

function RenderRadar({ radarData, timeValues, numberOfRadars }) {
  return (
    <ResponsiveContainer
      width="100%"
      height={300}
      margin={{
        left: 2,
        right: 2,
      }}
    >
      <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
        {/* <PolarGrid/> */}
        <PolarAngleAxis
          dataKey="label"
          tickFormatter={(tick) => (ticks.includes(tick) ? tick : "")}
        />
        <Radar
          name="clock"
          dataKey="circle"
          stroke="#8884d8"
          fill="none"
          fillOpacity={0.6}
          isAnimationActive={false}
        />
        {new Array(numberOfRadars + 1).fill(0).map((_, index) => (
          <Radar
            key={index}
            name="point"
            dataKey={index + "#point"}
            dot={<CustomizedDot fillColor={index + "#color"} />}
            fill="none"
            fillOpacity={0.6}
          />
        ))}
        <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1 }} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

export default function ClockBoard({
  data,
  timeLabelsChecked,
  qualitativeLabelsChecked,
  clockView,
}) {
  const [radarData, setRadarData] = useState([]);
  const [timeValues, setTimeValues] = useState([]);
  const [numberOfRadars, setNumberOfRadars] = useState(1);
  useEffect(() => {
    // responses
    const timeLabelIds = timeLabelsChecked.map(({ id }) => id);
    const qualitativeLabelIds = qualitativeLabelsChecked.map(({ id }) => id);
    let times = [];
    data.forEach((beep) => {
      const sendBeep = beep.time; // utc timestamp of filled in beep
      const timeAnswers = beep.table.filter((row) =>
        timeLabelIds.includes(row.id)
      ); // ids of the selected time answers
      const qualitativeAnswers = beep.table.filter((row) =>
        qualitativeLabelIds.includes(row.id)
      ); // ids of the selected qualitativeAnswers
      timeAnswers.forEach((answer) => {
        /*         const primaryScore = answer.primaryScore; // time in format HH:mm
        const roundedSec =
          Math.round(primaryScore / (interval * 60)) * (interval * 60); // round time to closest interval
        const time = moment.utc(roundedSec * 1000).format("HH:mm"); // parse it to right format
 */
        function roundTimeToNearest15Minutes(timeString) {
          if (typeof timeString !== "string") {
            new Error("Invalid time string format");
            return "00:00"
          }
          const [hours, minutes] = timeString.split(":").map(Number);

          if (
            isNaN(hours) ||
            isNaN(minutes) ||
            hours < 0 ||
            hours >= 24 ||
            minutes < 0 ||
            minutes >= 60
          ) {
            throw new Error("Invalid time string format");
          }

          // Calculate the nearest 15-minute interval
          const roundedMinutes = Math.round(minutes / 15) * 15;
          const roundedHours = hours + Math.floor(roundedMinutes / 60);
          const finalMinutes = roundedMinutes % 60;

          // Format the rounded time
          const roundedTime = `${String(roundedHours).padStart(
            2,
            "0"
          )}:${String(finalMinutes).padStart(2, "0")}`;
          return roundedTime;
        }

        // Example usage
        const timeString = answer.primaryScore;
        const time = roundTimeToNearest15Minutes(timeString);

        times.push({ time, info: { answer, sendBeep, qualitativeAnswers } });
      });
    });
    setTimeValues(times);

    // generate a new array with values "HH:mm"
    const timeslots = hours[clockView].flatMap((HH) =>
      minutes.map((mm) => `${HH}:${mm}`)
    );
    let maxIndex = 0;
    function randomIntFromInterval(min, max) {
      // min and max included
      return Math.floor(Math.random() * (max - min + 1) + min);
    }
    // render the radar data
    const radar = timeslots.map((timeSlot) => {
      let obj = {};
      times
        .filter(({ time }) => time === timeSlot)
        .forEach(({ info }, index) => {
          const rand = randomIntFromInterval(60, radius);
          obj[index + "#point"] = rand;
          obj[index + "#info"] = info;
          obj[index + "#color"] = getColor(info.answer.id);
          if (index > maxIndex) maxIndex = index;
        });
      obj["circle"] = radius; // outer circle
      return { ...obj, label: timeSlot };
    });
    setRadarData(radar);
    setNumberOfRadars(maxIndex);
  }, [data, timeLabelsChecked, clockView]);

  return (
    <RenderRadar
      radarData={radarData}
      timeValues={timeValues}
      numberOfRadars={numberOfRadars}
    />
  );
}
